import { useEffect, useRef, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { uploadDocuments, type DocumentUploadProgress } from '@/modules/documents'
import type { AccountingClient } from './accountingClientTypes'

interface Props {
  client: AccountingClient | null
  isOpen: boolean
  onClose: () => void
  onUploaded: () => void
}

export function AccountingClientUploadModal({ client, isOpen, onClose, onUploaded }: Props) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [progress, setProgress] = useState<DocumentUploadProgress | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ accepted: number; duplicates: number; rejected: number } | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setFiles([])
    setProgress(null)
    setUploading(false)
    setError(null)
    setResult(null)
  }, [isOpen, client?.party_id])

  const submit = async () => {
    if (!client || files.length === 0 || uploading) return
    setUploading(true)
    setError(null)
    setResult(null)
    try {
      const uploaded = await uploadDocuments(
        client.client_unit_id,
        files,
        setProgress,
        { purpose: 'bookkeeping' },
      )
      setResult(uploaded)
      setFiles([])
      onUploaded()
    } catch (uploadError) {
      console.error('[AccountingClientUploadModal] Upload error:', uploadError)
      const code = uploadError instanceof Error ? uploadError.message : 'upload_failed'
      const translated = t(`accounting.clientPortal.errors.${code}`)
      setError(translated.startsWith('accounting.') ? t('accounting.clientPortal.errors.upload') : translated)
    } finally {
      setUploading(false)
      setProgress(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const progressPercent = progress && progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0

  return <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={t('accounting.clients.upload.title', { client: client?.name || '' })}
    size="md"
    onSubmit={files.length > 0 && !result ? submit : undefined}
    submitLabel={t('accounting.clients.upload.confirm')}
    submitting={uploading}
    closeLabel={t('actions.close')}
  >
    <div className="space-y-5">
      <p className="text-sm text-slate-600 dark:text-slate-300">{t('accounting.clients.upload.description')}</p>
      {error && <p className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">{error}</p>}
      {result && <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"><p className="font-semibold">{t('accounting.clients.upload.completed')}</p><p className="mt-1">{t('accounting.clientPortal.upload.result', { accepted: String(result.accepted), duplicates: String(result.duplicates), rejected: String(result.rejected) })}</p></section>}
      {uploading && progress && <section className="rounded-xl border border-sky-200 bg-sky-50 p-4 dark:border-sky-900 dark:bg-sky-950/30"><div className="flex items-center justify-between gap-3 text-sm"><span className="min-w-0 truncate font-medium text-sky-900 dark:text-sky-100">{progress.fileName}</span><span className="shrink-0 font-semibold text-sky-700 dark:text-sky-300">{progress.current}/{progress.total}</span></div><div className="mt-3 h-2 overflow-hidden rounded-full bg-sky-100 dark:bg-sky-900"><div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${progressPercent}%` }} /></div></section>}
      {!result && <>
        <input ref={inputRef} type="file" multiple accept=".xml,.txt,.zip,application/xml,text/xml,text/plain,application/zip" className="hidden" onChange={(event) => setFiles(Array.from(event.target.files || []))} />
        <button type="button" disabled={uploading} onClick={() => inputRef.current?.click()} className="flex w-full flex-col items-center rounded-xl border border-dashed border-slate-300 px-5 py-8 text-center hover:border-emerald-500 hover:bg-emerald-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-emerald-950/20"><svg className="h-7 w-7 text-emerald-600 dark:text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" /></svg><span className="mt-3 text-sm font-semibold text-emerald-700 dark:text-emerald-300">{t('accounting.clients.upload.select')}</span><span className="mt-1 text-xs text-slate-500">{files.length > 0 ? t('accounting.clients.upload.selected', { count: String(files.length) }) : t('accounting.clientPortal.upload.hint')}</span></button>
      </>}
    </div>
  </Modal>
}
